import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as refreshTokenService } from '../services/auth.service';
import { buildAuthHeaders } from '../auth/headers';

export const axiosObject = axios.create();

function isPilotAccessToken(accessToken: string): boolean {
  try {
    return jwtDecode<{ is_pilot?: boolean }>(accessToken).is_pilot === true;
  } catch {
    return false;
  }
}

function rejectPilotToken(accessToken: string): void {
  void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
    syncAuthStoreFromToken(accessToken);
  });
}

axiosObject.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const language = sessionStorage.getItem('language') || 'ar';
    let accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');

    // Never attach an ESG-forbidden pilot token to an API request. The auth
    // store clears the session and exposes the localized Pilot Mode dialog.
    if (accessToken && isPilotAccessToken(accessToken)) {
      rejectPilotToken(accessToken);
      accessToken = null;
    }

    if (
      !config.url?.includes('/app_user/refresh_token') &&
      accessToken &&
      refreshToken
    ) {
      try {
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken.exp) {
          const expirationTime = decodedToken.exp * 1000;

          if (Date.now() >= expirationTime) {
            const response = await refreshTokenService({ refresh_token: refreshToken });
            if (response?.result?.access_token) {
              const refreshedAccessToken = String(response.result.access_token);
              accessToken = refreshedAccessToken;
              if (isPilotAccessToken(refreshedAccessToken)) {
                rejectPilotToken(refreshedAccessToken);
                accessToken = null;
              } else {
                sessionStorage.setItem('access_token', refreshedAccessToken);
                sessionStorage.setItem('refresh_token', response.result.refresh_token);
                void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                  syncAuthStoreFromToken(refreshedAccessToken);
                });
              }
            } else if (response?.access_token) {
              const refreshedAccessToken = String(response.access_token);
              accessToken = refreshedAccessToken;
              if (isPilotAccessToken(refreshedAccessToken)) {
                rejectPilotToken(refreshedAccessToken);
                accessToken = null;
              } else {
                sessionStorage.setItem('access_token', refreshedAccessToken);
                sessionStorage.setItem('refresh_token', response.refresh_token);
                void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                  syncAuthStoreFromToken(refreshedAccessToken);
                });
              }
            } else {
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('refresh_token');
              console.error('Failed to refresh token');
            }
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
      }
    }

    const authHeaders = buildAuthHeaders(accessToken);

    const updatedConfig: InternalAxiosRequestConfig = {
      ...config,
      params: {
        ...config.params,
        lang: language.toUpperCase(),
      },
      headers: {
        ...config.headers,
        ...authHeaders,
      } as AxiosRequestHeaders,
    };

    return updatedConfig;
  },
  (error) => {
    console.error('error: interceptors', error);
    return Promise.reject(error);
  },
);

axiosObject.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    if (status === 403) {
      const message =
        error.response?.data?.response?.message ??
        error.response?.data?.detail ??
        'You do not have permission to perform this action';
      console.warn('[403]', message);
    }

    return Promise.reject(error);
  },
);

export default axiosObject;
